import { IpDetectionResult } from "./ipDetection";

export interface VpnDetectionResult {
  vpnLikelihood: "none" | "low" | "medium" | "high";
  signals: {
    webrtcMismatch: boolean;
    timezoneMismatch: boolean;
    datacenterIp: boolean;
    multiplePublicIps: boolean;
  };
  details: {
    clientTimezone: string;
    geoCountry: string | null;
    webrtcPublicIps: string[];
    exitIp: string | null;
  };
}

function isPrivateIp(ip: string): boolean {
  return (
    ip.startsWith("10.") ||
    ip.startsWith("192.168.") ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(ip) ||
    ip.startsWith("127.") ||
    ip.startsWith("169.254.")
  );
}

const TIMEZONE_COUNTRY_MAP: Record<string, string[]> = {
  "Asia/Shanghai": ["CN"],
  "Asia/Tokyo": ["JP"],
  "Asia/Seoul": ["KR"],
  "America/New_York": ["US"],
  "America/Los_Angeles": ["US"],
  "America/Chicago": ["US"],
  "Europe/London": ["GB"],
  "Europe/Berlin": ["DE", "AT", "CH"],
  "Asia/Singapore": ["SG"],
  "Asia/Taipei": ["TW"],
  "Asia/Hong_Kong": ["HK"],
};

const DATACENTER_PREFIXES = [
  "34.", "35.", "104.196.", "104.197.", "104.198.", "104.199.",
  "52.", "54.", "18.", "3.",
  "13.", "20.", "40.", "51.", "52.",
  "157.240.", "31.13.",
  "198.41.", "104.16.", "104.17.", "104.18.", "104.19.",
];

export function detectVpn(ipResult: IpDetectionResult): VpnDetectionResult {
  const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const geoCountry = ipResult.backendGeo?.country || null;

  // Signal 1: WebRTC public IP mismatch
  const publicWebrtcIps = ipResult.webrtcLocalIps.filter((ip) => !isPrivateIp(ip) && !ip.includes(".local"));
  const exitIp = ipResult.backendIp || ipResult.ipifyV4 || ipResult.fallbackIp;
  const webrtcMismatch = publicWebrtcIps.length > 0 && exitIp != null && !publicWebrtcIps.includes(exitIp);

  // Signal 2: Timezone vs GeoIP country mismatch
  let timezoneMismatch = false;
  if (geoCountry && TIMEZONE_COUNTRY_MAP[clientTimezone]) {
    timezoneMismatch = !TIMEZONE_COUNTRY_MAP[clientTimezone].includes(geoCountry);
  }

  // Signal 3: Datacenter IP detection (heuristic)
  let datacenterIp = false;
  if (exitIp) {
    datacenterIp = DATACENTER_PREFIXES.some((prefix) => exitIp.startsWith(prefix));
  }

  // Signal 4: Multiple different public IPs
  const publicIps = new Set<string>();
  if (ipResult.backendIp) publicIps.add(ipResult.backendIp);
  if (ipResult.ipifyV4) publicIps.add(ipResult.ipifyV4);
  if (ipResult.fallbackIp) publicIps.add(ipResult.fallbackIp);
  const multiplePublicIps = publicIps.size > 1;

  // Calculate likelihood
  const score = (webrtcMismatch ? 3 : 0) + (timezoneMismatch ? 2 : 0) + (datacenterIp ? 1 : 0) + (multiplePublicIps ? 1 : 0);
  let vpnLikelihood: VpnDetectionResult["vpnLikelihood"] = "none";
  if (score >= 4) vpnLikelihood = "high";
  else if (score >= 2) vpnLikelihood = "medium";
  else if (score >= 1) vpnLikelihood = "low";

  return {
    vpnLikelihood,
    signals: { webrtcMismatch, timezoneMismatch, datacenterIp, multiplePublicIps },
    details: {
      clientTimezone,
      geoCountry,
      webrtcPublicIps: publicWebrtcIps,
      exitIp,
    },
  };
}
